import { Analytics } from '@cobliteam/analytics';
import { useDashboard } from '@cobliteam/dashboard/src/providers/DashboardProvider';
import { useTranslation } from '@cobliteam/i18n';
import Alert from '@cobliteam/ui/components/Alert';
import Typography from '@cobliteam/ui/components/Typography';
import Icon from '@cobliteam/ui/icons';
import { cn } from '@cobliteam/ui/utils';
import Feedback from '@cobliteam/ui-business/components/Feedback';
import type { ChatStatus, UIMessage } from 'ai';
import { nanoid } from 'nanoid';
import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { useAIAssistant } from '../store';
import { PromptInput, PromptInputSubmit, PromptInputTextarea, PromptInputToolbar } from './elements/PromptInput';

interface AIChatPromptInputProps {
  placeholder?: string;
  loading?: boolean;
  className?: string;
  sendMessage: (message: UIMessage) => Promise<void>;
  status: ChatStatus;
  stop: () => void;
  /** Analytics source identifier for tracking where events originate */
  source?: string;
}

const AIChatPromptInput = ({ placeholder, loading = false, className, sendMessage, status, stop, source = 'sidepanel' }: AIChatPromptInputProps) => {
  const { t } = useTranslation('ai');
  const [input, setInput] = useState('');
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isFocused, setIsFocused] = useState(true);
  const { createFakeThread, threadId, newThreadId, pendingInput, clearPendingInput } = useAIAssistant();
  const pendingAutoSubmitRef = useRef(false);

  useEffect(() => {
    if (pendingInput) {
      setInput(pendingInput);
      clearPendingInput();
      pendingAutoSubmitRef.current = true;
    }
  }, [pendingInput, clearPendingInput]);

  const { environment } = useDashboard();
  const isDev = environment?.isDev;
  const resolvedPlaceholder = placeholder ?? t('prompt_placeholder');
  const isSendingDisabled = status === 'ready' && !input.trim();
  const activeThreadId = threadId || newThreadId;

  const ensureThreadExists = useCallback(async () => {
    if (threadId) return false;

    await createFakeThread({ threadId: activeThreadId });
    return true;
  }, [activeThreadId, createFakeThread, threadId]);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setSubmitError(null);

      if (status !== 'ready') {
        Analytics.track('AI - Chat - Generation Stopped', { source });
        stop();
        return;
      }

      const trimmedInput = input.trim();

      if (!trimmedInput) {
        Alert.error(t('please_type_message'));
        return;
      }

      try {
        await ensureThreadExists();

        Analytics.track('AI - Chat - Message Sent', { prompt: trimmedInput, source });
        await sendMessage({ role: 'user', id: nanoid(), parts: [{ type: 'text', text: trimmedInput }] });
        setInput('');
      } catch (err) {
        setSubmitError(t('send_failed'));
        if (isDev) {
          const message = err instanceof Error ? err.message : String(err);
          // eslint-disable-next-line no-console
          console.error('[AIChatPromptInput] sendMessage failed:', message, err);
        }
      }
    },
    [ensureThreadExists, input, isDev, sendMessage, source, status, stop, t]
  );

  // Auto-submit when pendingInput was consumed from the store
  useEffect(() => {
    if (pendingAutoSubmitRef.current && input.trim() && status === 'ready') {
      pendingAutoSubmitRef.current = false;
      const syntheticEvent = { preventDefault: () => undefined } as React.FormEvent;
      handleSubmit(syntheticEvent);
    }
  }, [input, handleSubmit, status]);

  if (loading) return null;

  return (
    <div data-testid='ai-chat-input' className={cn('flex w-full shrink-0 flex-col gap-1 px-4', className)}>
      <div className='relative flex flex-col gap-0.5'>
        <div
          className={cn('relative flex items-center gap-2 rounded-xl shadow-md transition-shadow', isFocused && 'ring-2 ring-brand-8')}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
        >
          <PromptInput onSubmit={handleSubmit} className='relative w-full border-gray-6 bg-white'>
            <PromptInputTextarea
              autoFocus
              placeholder={resolvedPlaceholder}
              value={input}
              onChange={(e) => {
                setInput(e.target.value);
                setSubmitError(null);
              }}
              disabled={status !== 'ready'}
              className='pb-2'
            />
            <PromptInputToolbar className='justify-end px-3.5 pt-0 pb-2.5'>
              <div className='flex items-center gap-1.5'>
                <PromptInputSubmit disabled={isSendingDisabled} status={status} variant='solid' />
              </div>
            </PromptInputToolbar>
          </PromptInput>
        </div>
        {submitError && (
          <span className='text-danger-11 text-xs' role='alert'>
            {submitError}
          </span>
        )}
      </div>
      <div className='flex h-8 items-center justify-between'>
        <Feedback source='ai-chat' showTooltip={false}>
          <Typography
            variant='text-sm'
            color='secondary'
            className='flex shrink-0 cursor-pointer items-center gap-1 text-gray-9'
            onClick={() => Analytics.track('AI - Chat - Feedback Button Clicked')}
          >
            <Icon name='megaphone' className='text-gray-9' />
            Enviar sugestão?
          </Typography>
        </Feedback>
        <div className='flex items-center justify-start gap-1 text-gray-9 text-xs italic leading-4'>*Cobli IA pode cometer erros.</div>
      </div>
    </div>
  );
};

export default memo(AIChatPromptInput);
